import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';
import { mergeProps } from '../utils/with-default-props';
import { NativeProps, withNativeProps } from '../utils/native-props';
import styles from './button.module.scss';

const classPrefix = `adm-button`;

export type ButtonProps = {
  size?: 'xs' | 's' | 'm' | 'l' | 'xl';
  block?: boolean;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void | Promise<void>;
  type?: 'submit' | 'reset' | 'button';
  shape?: 'default' | 'rounded' | 'rectangular';
  children?: React.ReactNode;
} & NativeProps<'--text-color' | '--background-color' | '--border-radius' | '--border-width' | '--border-style' | '--border-color'>;

export type ButtonRef = {
  nativeElement: HTMLButtonElement | null;
};

const defaultProps: ButtonProps = {
  size: 'm',
  block: false,
  disabled: false,
  type: 'button',
  shape: 'default',
};

export const Button = forwardRef<ButtonRef, ButtonProps>((p, ref) => {
  const props = mergeProps(defaultProps, p);
  const nativeButtonRef = useRef<HTMLButtonElement>(null);
  const { disabled } = props;

  useImperativeHandle(ref, () => ({
    get nativeElement() {
      return nativeButtonRef.current;
    },
  }));

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    if (!props.onClick) return;
    props.onClick(e);
  };

  return withNativeProps(
    props,
    <button
      ref={nativeButtonRef}
      type={props.type}
      onClick={handleClick}
      className={classNames(
        styles[classPrefix],
        {
          [styles[`${classPrefix}-block`]]: props.block,
          [styles[`${classPrefix}-disabled`]]: disabled,
          [styles[`${classPrefix}-xs`]]: props.size === 'xs',
          [styles[`${classPrefix}-s`]]: props.size === 's',
          [styles[`${classPrefix}-m`]]: props.size === 'm',
          [styles[`${classPrefix}-l`]]: props.size === 'l',
          [styles[`${classPrefix}-xl`]]: props.size === 'xl',
        },
        styles[`${classPrefix}-shape-${props.shape}`]
      )}
      style={props.style}
      disabled={disabled}
    >
      {props.children}
    </button>
  );
});
export default Button;
